import React from 'react';
import { type Node } from 'reactflow';
import { Grid } from '@mui/material';

import {
	BooleanField,
	DateField,
	EditorHtmlField,
	ModuleSearchField,
	NumberTextField,
	SearchSelectField,
	SelectField,
	SimpleTextField,
	useSettings
} from 'app/main/components';
import { type ModelType } from 'app/main/components/hooks/useModel';
import { type WbsRootNode, type WbsNode } from './useWbsState';

interface WbsNodeViewerContentProps {
	selectedNode: Node<WbsRootNode | WbsNode>;
	model: any;
	isEdit: boolean;
	sendChange: ModelType['sendChange'];
}

export default function WbsNodeViewerContent({ selectedNode, model, isEdit, sendChange }: WbsNodeViewerContentProps) {
	const { t } = useSettings();

	return (
		<Grid container spacing={1}>
			<Grid size={12}>
				<SimpleTextField
					name="wbsn_title"
					label={t('WBS_NODES_FIELD_TITLE')}
					value={model.wbsn_title}
					isEdit={isEdit}
					sendChange={sendChange}
					required
					validationError={t('ERROR_TEXT_REQUIRED')}
				/>
			</Grid>
			<Grid size={{ sm: 6, xs: 12 }}>
				<SelectField
					name="wbsn_is_leaf"
					label={t('WBS_NODES_FIELD_TYPE')}
					options={[
						{ value: 'branchNode', title: t('WBS_NODES_FIELD_TYPE_BRANCH') },
						{ value: 'leafNode', title: t('WBS_NODES_FIELD_TYPE_LEAF') }
					]}
					value={selectedNode.type || 'leafNode'}
					isEdit={isEdit && !selectedNode.data?.node?.wbsn_children}
					sendChange={(name, value) => sendChange(name, value === 'leafNode')}
					disableEmptyOption
					required
					validationError={t('ERROR_TEXT_REQUIRED')}
				/>
			</Grid>
			<Grid size={{ sm: 6, xs: 12 }}>
				<NumberTextField
					name="wbsn_order"
					label={t('WBS_NODES_FIELD_ORDER')}
					value={model.wbsn_order}
					isEdit={isEdit}
					sendChange={sendChange}
				/>
			</Grid>
			{model.wbsn_is_leaf ? (
				<>
					<Grid size={12}>
						<SearchSelectField
							name="wbsn_acty_id"
							label={t('WBS_NODES_FIELD_ACTIVITY_TYPE')}
							systable="activities_types"
							value={model.wbsn_acty_id}
							isEdit={isEdit}
							sendChange={sendChange}
							afterChange={newValue => {
								if (!model.wbsn_title || model.wbsn_title === model.acty_title)
									sendChange('wbsn_title', newValue.acty_title);
								sendChange('acty_title', newValue.acty_title);
							}}
						/>
					</Grid>
					<Grid size={{ sm: 6, xs: 12 }}>
						<DateField
							name="wbsn_d_from"
							label={t('WBS_NODES_FIELD_DATE_FROM_EXPECTED')}
							value={model.wbsn_date}
							isEdit={isEdit}
							sendChange={sendChange}
						/>
					</Grid>
					<Grid size={{ sm: 6, xs: 12 }}>
						<DateField
							name="wbsn_d_to"
							label={t('WBS_NODES_FIELD_DATE_TO_EXPECTED')}
							value={model.wbsn_d_to}
							isEdit={isEdit}
							sendChange={sendChange}
						/>
					</Grid>
					<Grid size={{ sm: 6, xs: 12 }}>
						<NumberTextField
							name="wbsn_hours_expected"
							label={t('WBS_NODES_FIELD_HOURS_EXPECTED')}
							value={model.wbsn_hours_expected}
							measureUnit="h"
							isEdit={isEdit}
							sendChange={sendChange}
						/>
					</Grid>
					<Grid size={12}>
						<ModuleSearchField
							name="wbsn_user_assigned_uid"
							label={t('WBS_NODES_FIELD_USER_ASSIGNED')}
							module="users"
							uid={model.wbsn_user_assigned_uid}
							isEdit={isEdit && !model.wbsn_inherit_user_assigned}
							sendChange={sendChange}
						/>
					</Grid>
					<Grid size={12}>
						<BooleanField
							name="wbsn_inherit_user_assigned"
							label={t('WBS_NODES_FIELD_INHERIT_USER_ASSIGNED')}
							value={model.wbsn_inherit_user_assigned}
							isEdit={isEdit}
							sendChange={sendChange}
						/>
					</Grid>
				</>
			) : (
				<></>
			)}
			<Grid size={12}>
				<EditorHtmlField
					name="wbsn_descriptionhtml"
					label={t('WBS_NODES_FIELD_DESCRIPTION')}
					value={model.wbsn_descriptionhtml}
					isEdit={isEdit}
					sendChange={sendChange}
				/>
			</Grid>
		</Grid>
	);
}
